import { atlasCell, onAtlasReset } from './atlas.ts';
/* THE FORAGER HAS BONES (LS-184).
 *
 * The player was a 13-pixel sprite with two walking frames per facing, and
 * the sickle an arc drawn beside it. That cannot sell a chain, a thrust or a
 * spin: the body never leans, the arm never reaches, the feet never plant.
 * This is a small 2D skeleton -- hip, chest, neck, head, two arms, two legs --
 * posed by functions of the player's state (the walk phase, the swing's
 * kind and angle, the dodge, the reel) and rendered procedurally at pixel
 * scale: limbs as capsules, the body and head as discs, a one-pixel outline,
 * everything snapped to whole pixels and cached per quantised pose, so a
 * frame costs a lookup. The sickle is a blade attached to the hand, rotated
 * with the arm along the swing's real angle.
 *
 * Three views -- side (mirrored for left), front (down) and back (up) --
 * share one figure; the arms reach in SCREEN space, so a swing to the right
 * is the right arm out to the right in every view, which is what a 3/4
 * top-down figure does.
 *
 * Everything here is a pure function of its inputs: the same state draws the
 * same pixels, which the soak relies on. */
import { PALETTE as P, UI, wetTone } from './art.ts';
import { domeFromMask } from './light.ts';
import { pose3d } from './rig3d.ts';

export type XY = [number, number];
/* EIGHT FACINGS (LS-194). Vijay: "upgrade all sprites to have 8 directions
 * of movement". A skeleton does not need eight sheets: the four diagonals are
 * the front or back view YAWED -- the body narrowed toward the way it faces,
 * the near shoulder and hip stepped forward, the far ones back and a touch
 * higher, the head turned with the eyes carried across. */
export type Facing = 'up' | 'down' | 'left' | 'right' | 'up-right' | 'up-left' | 'down-right' | 'down-left';
import { SWING, axeMotion } from './swing.ts';
export { SWING };

export type RigState = {
  dir: Facing;
  weapon?: string;       // the kind in hand -- sickle, spear, axe, torch, fist (LS-238)
  gear?: string;         // 'body' or 'body+feet' kinds worn: the tunic and the boots take their colour (LS-239)
  anim: number;          // the player's frame counter (breath, glances)
  stride?: number;       // the gait's phase, in radians of ground covered
  moving: boolean;
  attack: number;        // SWING.total .. 0 while the sickle is out
  swingKind: string;     // fore, back, thrust, spin, chop, heave
  swingAngle: number;    // absolute, radians, screen space
  seated?: boolean;      // in a hull (LS-383): the hip low, the knees up, the feet forward -- the body rides, it does not stand
  dodge: number;         // frames of dodge left
  dodgeMax?: number;     // frames a dodge lasts, so the roll's progress is a fraction (LS-210)
  ragdoll?: number;      // frames of ragdoll left after a hit (LS-212)
  ragdollMax?: number;
  ragdollDir?: XY;       // the way the body flew (and the way a flinch was shoved)
  flinch?: number;       // frames of the small hit's hurt pose left (LS-212)
  flinchMax?: number;
  recoil?: number;       // the blade kicked back off stone or metal (LS-213)
  recoilMax?: number;
  recoilDir?: XY;        // away from what it struck
  dodgeDir: XY;
  reel: boolean;         // knocked back
  lit: boolean;          // carrying fire
  wet?: boolean;         // soaked, or in the rain: the cloth and the skin go dark and cool (2026-09-11)
  wading?: boolean;
  charge?: number;       // frames the button has been held toward a spin (LS-207)
};

export type Pose = {
  view: 'side' | 'front' | 'back';
  flip: boolean;
  hip: XY; chest: XY; neck: XY; head: XY;
  sh: [XY, XY]; el: [XY, XY]; ha: [XY, XY];   // [near, far] for side; [left, right] for front/back
  kn: [XY, XY]; ft: [XY, XY];
  sickle: { at: XY; ang: number; len: number } | null;
  weapon?: string;       // what the blade painter draws in the hand (LS-238)
  gear?: string;         // what is worn, for the tunic and the boots (LS-239)
  trail: { at: XY; ang: number; len: number; w?: number; dim?: boolean }[];   // the smear: nearest segment first
  swingArm: 0 | 1;       // which arm holds the sickle this frame
  yaw: number;           // -1 turned to screen-left, 0 square on, +1 to screen-right
  lit: boolean;
  key: string;
};

/* The cell: 32x32, drawn at (x - 9, y - 10) of a 13x13 body box, so the feet
 * sit on the body's bottom row and a thrust has room to reach on every side. */
/* THE CELL IS 48x48 NOW (LS-202). Vijay: "the player weapon and visual
 * effects need to be much bigger". A seven-pixel blade on a thirteen-pixel
 * body is a paring knife, and at the game's zoom it read as a glint at the
 * end of the arm rather than as the thing you fight with. The blade is
 * eleven pixels now with a longer haft and a thicker edge, and that does not
 * fit in a 32-pixel cell with the arm extended: the cell is 48, the body box
 * still sits with its feet on the floor row, and every offset that placed
 * the cell on the body moved by the same four pixels. */
export const RIG_W = 48, RIG_H = 48, RIG_OX = -17, RIG_OY = -18;
/* ...and 48, not 40: at 40 the tip of a thrust -- shoulder at 2.6, reach 8.6,
 * blade 10.5 -- landed 1.7 pixels past the edge and the soak said so. Two
 * and a quarter times the pixels of the old cell per pose, all of it cached
 * per pose key, so the cost is memory in the pose cache and not frame time. */
/** Rows of the cell above the water when wading: the feet are under it. */
export const RIG_WADE_H = 29;
const CX = RIG_W / 2, FLOOR = RIG_OY * -1 + 13 + 0.5;   // 20, 27.5: the feet on the body's bottom row

const q = (v: number) => Math.round(v * 2) / 2;   // half-pixel pose quantisation for the cache

/* ------------------------------------------------------------------ ik --- */

export const sub = (a: XY, b: XY): XY => [a[0] - b[0], a[1] - b[1]];
export const add = (a: XY, b: XY): XY => [a[0] + b[0], a[1] + b[1]];
export const mul = (a: XY, k: number): XY => [a[0] * k, a[1] * k];
const len = (a: XY) => Math.hypot(a[0], a[1]);

/** Two-bone IK: the middle joint for a limb from `root` reaching for `target`
 *  with segments l1 and l2, bending to `side` (+1 or -1) of the root->target
 *  line. The target is pulled in if the limb cannot reach. */
export function ik2(root: XY, target: XY, l1: number, l2: number, side: number): [XY, XY] {
  let d = sub(target, root);
  let L = len(d) || 0.001;
  const max = l1 + l2 - 0.05;
  if (L > max) { d = mul(d, max / L); L = max; target = add(root, d); }
  const cosA = Math.max(-1, Math.min(1, (l1 * l1 + L * L - l2 * l2) / (2 * l1 * L)));
  const ang = Math.atan2(d[1], d[0]) + Math.acos(cosA) * side;
  return [[root[0] + Math.cos(ang) * l1, root[1] + Math.sin(ang) * l1], target];
}

export const ease = (t: number) => t * t * (3 - 2 * t);
const UPPER_ARM = 3.2, FORE_ARM = 3.4, THIGH = 3.4, SHIN = 3.2;

/* THE SKELETON IS THREE-DIMENSIONAL NOW (LS-198). `pose3d` builds the figure
 * in character space, rotates it by the heading and projects it through the
 * game's camera; what it returns is this same `Pose`, so the painter below is
 * untouched. The 2D poser stays behind `rigMode('2d')` -- the suites compare
 * the two, and it is the fallback if a screen ever needs the old one. */
let MODE: '3d' | '2d' = '3d';
export function rigMode(m?: '3d' | '2d') { if (m) MODE = m; return MODE; }

/** Pose the figure for a state. */
export function poseFor(s: RigState): Pose {
  const p = MODE === '3d' ? pose3d(s) : poseFor2d(s);
  p.weapon = s.weapon || 'sickle'; p.gear = s.gear || ''; p.key += '|' + p.weapon + '|' + p.gear;   // the kind in hand and what is worn are part of the sprite (LS-238, LS-239)
  (p as any).wet = !!s.wet; if (s.wet) p.key += '|w';   // the wet state, whichever poser made the pose (2026-09-11)
  return p;
}

/** The 2D poser: the front, back and side views with the diagonals yawed. */
export function poseFor2d(s: RigState): Pose {
  const diag = s.dir.indexOf('-') > 0;
  const vert = diag ? s.dir.slice(0, s.dir.indexOf('-')) : s.dir;
  let view: Pose['view'] = diag ? (vert === 'up' ? 'back' : 'front')
    : s.dir === 'down' ? 'front' : s.dir === 'up' ? 'back' : 'side';
  let flip = s.dir === 'left';
  // the diagonals are the front or back view turned toward the screen side
  let yaw = diag ? (s.dir.endsWith('right') ? 1 : -1) : 0;
  const t = s.anim;
  const inSwing = s.attack > 0;
  const u = inSwing ? Math.min(1, Math.max(0, (SWING.live - s.attack) / (SWING.live - SWING.follow))) : 0;   // 0..1 across the active arc
  /* THE SPIN TURNS THE FIGURE. The blade's angle says where the body faces
   * this frame, so the forager visibly comes round through side, front and
   * back instead of standing still while an arm whirls. */
  if (inSwing && s.swingKind === 'spin' && s.attack <= SWING.live) {
    const a = ((s.swingAngle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    if (a < Math.PI / 4 || a > Math.PI * 7 / 4) { view = 'side'; flip = false; }
    else if (a < Math.PI * 3 / 4) view = 'front';
    else if (a < Math.PI * 5 / 4) { view = 'side'; flip = true; }
    else view = 'back';
  }
  const side = view === 'side';
  // ---- the gait
  const walking = s.moving && !inSwing && s.dodge <= 0 && !s.reel;
  // the gait runs on the ground covered, not on the clock (LS-194)
  const ph = walking ? (s.stride ?? t * 0.30) : 0;
  const bob = walking ? Math.abs(Math.sin(ph)) * 1.0 : 0;   // a whole pixel at the top of the stride: 0.9 * 0.9 rounded away and the head stood on one row through all twelve frames (round 53's art lens)
  const breathe = walking ? 0 : Math.sin(t / 22) * 0.9;   // a whole pixel at the top of the breath: 0.35 rounded to nothing and the forager stood like a statue (the art lens, round 39)
  // an occasional glance while standing
  const glanceT = ((t % 260) + 260) % 260;
  const glance = (!walking && !inSwing && s.dodge <= 0 && glanceT > 200 && glanceT < 230) ? (glanceT < 206 ? ease((glanceT - 200) / 6) : glanceT > 224 ? 1 - ease((glanceT - 224) / 6) : 1) : 0;
  // ---- the trunk, with lean, twist and crouch
  let lean: XY = [0, 0], crouch = 0, twist = 0;
  const axe = axeMotion(s.swingKind, s.attack);
  /* A SWEEP TAKES ITS HEIGHT FROM `axeMotion` BUT ITS ARC FROM THE SWING
   * (2026-09-10, LS-336). `verticalSwing` was simply "axeMotion has something
   * to say", and once the torch's sweep was given a height profile it fell in
   * with the chop and the heave -- which meant no arc smear at all, and three
   * trail strokes laid at the same angle as the blade, invisible. It is drawn
   * with a low held hand AND a horizontal smear: judged on the pose sheet
   * (test/_rigsheet.ts ROWS=sweep), where without this it read as a pose. */
  const verticalSwing = !!axe && s.swingKind !== 'sweep';
  if (s.dodge > 0) {
    // a roll: tuck through the middle of the dash, unfold at the end
    const r = 1 - s.dodge / 9;                       // 0 at the start, 1 at the end
    const tuck = Math.sin(Math.min(1, r * 1.15) * Math.PI);
    lean = [s.dodgeDir[0] * 3.5, s.dodgeDir[1] * 1.5];
    crouch = tuck * 4;
  } else if (s.reel) lean = [0, -1.5];
  if (inSwing) {
    const cosA = Math.cos(s.swingAngle), sinA = Math.sin(s.swingAngle);
    if (s.attack > SWING.live) {                        // wind-up: pull back
      const w = (s.attack - 14) / 4;
      twist = -0.8 * w; lean = [-cosA * 1.2 * w, -sinA * 0.6 * w];
      if (s.swingKind === 'thrust') crouch = 1.2 * w;
    } else if (s.attack > SWING.follow) {               // the sweep: lean in
      const push = s.swingKind === 'thrust' ? 1.8 : s.swingKind === 'spin' ? 0 : s.swingKind === 'sweep' ? 0.5 : 1.0;   // a sweep leans out least: the arc does the covering (LS-336)
      twist = s.swingKind === 'fore' ? 0.8 : s.swingKind === 'back' ? -0.8 : 0;
      lean = [cosA * push, sinA * push * 0.5];
      if (s.swingKind === 'thrust') crouch = 0.8;
    } else {                                            // the settle
      const w = s.attack / SWING.follow;
      lean = [cosA * 0.5 * w, sinA * 0.25 * w];
    }
  }
  if (axe) {
    crouch = axe.crouch;
    twist = 0;
  }
  if (s.seated) crouch = 3.2;   // seated in a hull: the trunk drops onto the thwart
  const hip: XY = [CX + lean[0] * 0.35, FLOOR - 6 - bob + crouch * 0.5 + (s.seated ? 1.5 : 0)];
  const chest: XY = [CX + lean[0] * 0.8 + twist * 0.6 + (walking ? Math.cos(ph) * 0.35 : 0), FLOOR - 9.5 - bob - breathe + lean[1] + crouch];   // the whole breath, not half: half a pixel rounded away
  const neck: XY = [chest[0] + lean[0] * 0.15, chest[1] - 2];
  const head: XY = [neck[0] + lean[0] * 0.3 + glance * (flip ? -1 : 1) + (walking ? Math.cos(ph - 0.6) * 0.25 : 0), neck[1] - 2.6 + crouch * 0.4 + (s.dodge > 0 ? crouch * 0.5 : 0)];
  // ---- the legs: foot targets from the gait, knees from IK
  const kneeSide = flip ? 1 : -1;                     // the knee bends forward in side view
  let ftA: XY, ftB: XY;
  if (side) {
    if (walking) {
      const swingA = Math.cos(ph), swingB = Math.cos(ph + Math.PI);
      const liftA = Math.max(0, Math.sin(ph)) * 2.2, liftB = Math.max(0, Math.sin(ph + Math.PI)) * 2.2;
      ftA = [CX + swingA * 3.2, FLOOR - liftA]; ftB = [CX + swingB * 3.2, FLOOR - liftB];
    } else if (s.dodge > 0) {
      const sgn = Math.sign(lean[0] || 1);
      ftA = [CX + sgn * 3.5, FLOOR - crouch * 0.8]; ftB = [CX - sgn * 2.5, FLOOR - crouch * 1.4];
    } else if (s.reel) { ftA = [CX + 3, FLOOR]; ftB = [CX - 3.5, FLOOR]; }
    else if (s.seated) { const f = flip ? -1 : 1; ftA = [CX + f * 4.6, FLOOR - 2.4]; ftB = [CX + f * 3.4, FLOOR - 1.8]; }   // the legs out in front, the knees up
    else if (inSwing && s.swingKind === 'thrust' && s.attack <= SWING.live) { const f = flip ? -1 : 1; ftA = [CX + f * 4, FLOOR]; ftB = [CX - f * 2, FLOOR]; }
    else { ftA = [CX + 1.2, FLOOR]; ftB = [CX - 1.4, FLOOR]; }
  } else {
    if (walking) {
      /* WALKING TOWARD YOU IS STILL WALKING (LS-194 follow-up). Vijay:
       * "walking animation looks very strange". The front and back gait moved
       * the feet UP AND DOWN and swayed the whole body sideways: no step was
       * ever taken, so the figure paddled on the spot and waddled while it
       * did. A step seen from the front is mostly foreshortened DEPTH -- the
       * leading foot lands lower on the screen and a little wide, the
       * trailing one rises behind the body -- so the feet travel in y with
       * the lift, and the sway is gone. */
      const swA = Math.cos(ph), swB = Math.cos(ph + Math.PI);
      const liftA = Math.max(0, Math.sin(ph)) * 1.9, liftB = Math.max(0, Math.sin(ph + Math.PI)) * 1.9;
      ftA = [CX - 2.1 - swA * 0.5, FLOOR - liftA + swA * 1.5];
      ftB = [CX + 2.1 + swB * 0.5, FLOOR - liftB + swB * 1.5];
    } else if (s.dodge > 0) { ftA = [CX - 3, FLOOR - crouch * 0.8]; ftB = [CX + 3, FLOOR - crouch * 0.8]; }
    else if (s.reel) { ftA = [CX - 3.5, FLOOR]; ftB = [CX + 3.5, FLOOR]; }
    else if (s.seated) { ftA = [CX - 2.8, FLOOR - 2.2]; ftB = [CX + 2.8, FLOOR - 2.2]; }   // seated, facing the camera or away: the knees up either side
    else { ftA = [CX - 2.2, FLOOR]; ftB = [CX + 2.2, FLOOR]; }
  }
  const hipA: XY = side ? [hip[0] - 0.6, hip[1]] : [hip[0] - 1.6, hip[1]];
  const hipB: XY = side ? [hip[0] + 0.6, hip[1]] : [hip[0] + 1.6, hip[1]];
  const [knA] = ik2(hipA, ftA, THIGH, SHIN, side ? kneeSide : -1);
  const [knB] = ik2(hipB, ftB, THIGH, SHIN, side ? kneeSide : 1);
  let kn: [XY, XY] = [knA, knB], ft: [XY, XY] = [ftA, ftB];
  // ---- the arms: hand targets, elbows from IK
  const shL: XY = side ? [chest[0] - 0.4, chest[1] + 0.4] : [chest[0] - 2.6, chest[1] + 0.4];
  const shR: XY = side ? [chest[0] + 0.4, chest[1] + 0.4] : [chest[0] + 2.6, chest[1] + 0.4];
  const armSwing = walking ? Math.cos(ph + Math.PI) * 2.4 : 0;   // opposite the near leg
  // seen from the front or back an arm swings into depth, which is mostly y
  const armDepth = walking && !side ? Math.cos(ph + Math.PI) * 1.5 : 0;
  let haL: XY, haR: XY;
  if (side) {
    haL = [shL[0] - 0.4 - armSwing * 0.8, shL[1] + 6.0 - Math.abs(armSwing) * 0.25];
    haR = [shR[0] + 0.4 + armSwing, shR[1] + 6.0 - Math.abs(armSwing) * 0.3];
  } else {
    haL = [shL[0] - 1.0 - Math.abs(armSwing) * 0.15, shL[1] + 6.0 + armDepth];
    haR = [shR[0] + 1.0 + Math.abs(armSwing) * 0.15, shR[1] + 6.0 - armDepth];
  }
  if (s.reel) { haL = [shL[0] - 4.5, shL[1] - 2.5]; haR = [shR[0] + 4.5, shR[1] - 2.5]; }
  if (s.dodge > 0) { const sgn = Math.sign(lean[0] || 1); haL = [shL[0] - sgn * 2 - 1, shL[1] + 3 - crouch * 0.6]; haR = [shR[0] + sgn * 3, shR[1] + 2 - crouch * 0.6]; }
  // a left-facing figure is the right-facing one mirrored through the cell's
  // middle -- the BODY, not the canvas, so the arm below can still reach in
  // screen space and the blade points where the swing goes
  /* THE TURN (LS-194). Everything is posed square-on and then rotated: the
   * silhouette narrows about the middle, the whole body leans a pixel the way
   * it faces, and the two sides part -- the near arm and leg come forward and
   * down, the far ones step back and rise, which is what depth looks like at
   * this scale. Done here, before the mirror, so a diagonal costs one pose,
   * not a second sheet. */
  if (yaw) {
    /* HOW FAR TO TURN. At thirteen pixels tall a polite rotation is invisible:
     * the first pass narrowed the body to 0.7 and leaned it one pixel, and on
     * the sheet down-right and down were the same picture. The turn has to be
     * a SILHOUETTE change -- the body narrow, the near shoulder and boot a
     * clear two pixels ahead of the far ones, the head carried over the
     * leading foot -- because at this size the outline is all the reader
     * gets. */
    const squeeze = 0.6, shift = yaw * 1.7;
    const turn = (pt: XY, depth = 0, rise = 0) => { pt[0] = CX + (pt[0] - CX) * squeeze + shift + depth * yaw; pt[1] += rise; };
    turn(hip); turn(chest, 0.3); turn(neck, 0.5); turn(head, 1.1);
    // the limb on the side we face is the near one
    const near = yaw > 0 ? 1 : 0;
    const parts: [XY, number][] = [[shL, near === 0 ? 1 : 0], [shR, near === 1 ? 1 : 0], [haL, near === 0 ? 1 : 0], [haR, near === 1 ? 1 : 0]];
    for (const [pt, isNear] of parts) turn(pt, isNear ? 2.2 : -1.9, isNear ? 0.4 : -0.8);
    kn = [[...kn[0]] as XY, [...kn[1]] as XY]; ft = [[...ft[0]] as XY, [...ft[1]] as XY];
    for (const i of [0, 1]) {
      const isNear = i === near ? 1 : 0;
      turn(kn[i], isNear ? 1.9 : -1.6, isNear ? 0.3 : -0.6);
      turn(ft[i], isNear ? 2.3 : -2.0, isNear ? 0.4 : -0.9);
    }
  }
  const mx = (pt: XY): XY => [2 * CX - pt[0], pt[1]];
  if (flip) {
    for (const pt of [hip, chest, neck, head, shL, shR, haL, haR]) pt[0] = 2 * CX - pt[0];
    kn = [mx(kn[0]), mx(kn[1])]; ft = [mx(ft[0]), mx(ft[1])];
  }
  let ha: [XY, XY] = [haL, haR];
  // ---- the sickle arm: reaches along the swing's real angle, in screen space
  let sickle: Pose['sickle'] = null;
  let trail: Pose['trail'] = [];
  let swingArm: 0 | 1 = side ? 0 : 1;
  if (inSwing) {
    const a = s.swingAngle;
    const cosA = Math.cos(a), sinA = Math.sin(a);
    // the arm on the side the blade is going; in side view the near arm
    swingArm = side ? (flip ? 1 : 1) : (cosA < 0 ? 0 : 1);
    if (side) swingArm = 1;
    const sh = swingArm === 0 ? shL : shR;
    let reach: number, ang = a;
    if (s.attack > SWING.live) {                       // wind-up: the arm pulled back
      const w = (s.attack - 14) / 4;
      const back = s.swingKind === 'thrust' ? Math.PI : Math.PI * 0.75 * (s.swingKind === 'back' ? -1 : 1);
      ang = a + back * w; reach = 3.5 + (1 - w) * 2;
    } else if (s.attack > SWING.follow) {
      reach = s.swingKind === 'thrust' ? 8.2 : 7.2;
    } else {                                           // the settle: the arm drops back
      const w = s.attack / SWING.follow;
      reach = 3 + 3 * w; ang = a + (1 - w) * (s.swingKind === 'back' ? -0.6 : 0.6);
    }
    let lift = 0;
    if (axe) {
      // Project the raised/underhand axe above its ground-plane aim.
      lift = axe.lift;
      reach = axe.reach;
      ang = a;
    }
    const hand: XY = [sh[0] + Math.cos(ang) * reach, sh[1] + Math.sin(ang) * reach * 0.8 - lift];
    ha[swingArm] = hand;
    // the blade lags the hand a little and leaves a trail through the sweep
    const sweep = verticalSwing || s.swingKind === 'thrust' ? 0 : s.swingKind === 'back' ? -1 : 1;
    const lag = s.attack > SWING.follow && s.attack <= SWING.live ? 0.18 * sweep : 0;
    sickle = { at: hand, ang: ang - lag, len: s.swingKind === 'thrust' ? 10.5 : 11 };
    if (sweep && s.attack > SWING.follow - 4 && s.attack <= SWING.live) {
      // one faint stroke a step behind the blade: a smear, not a second blade
      const pa = ang - lag - sweep * 0.3;
      trail.push({ at: [sh[0] + Math.cos(pa) * reach, sh[1] + Math.sin(pa) * reach * 0.8], ang: pa, len: 9 });
    }
    if (axe && s.attack > SWING.follow - 2 && s.attack < SWING.live) {
      for (const k of [1, 2, 3]) {
        if (s.attack + k > SWING.live) continue;
        const previous = axeMotion(s.swingKind, s.attack + k)!;
        const previousU = Math.max(0, Math.min(1, (SWING.live - s.attack - k) / (SWING.live - SWING.follow)));
        const pa = a - (s.swingKind === 'heave' ? (u - previousU) * 0.9 : s.swingKind === 'sweep' ? (u - previousU) * 3.0 : 0);   // a sweep's strokes lag along its own three radians
        trail.push({ at: [sh[0] + Math.cos(pa) * previous.reach, sh[1] + Math.sin(pa) * previous.reach * 0.8 - previous.lift], ang: pa, len: 7 - k * 0.5, w: 2.1 - k * 0.3, dim: k > 1 });
      }
    }
    // the off arm counters the sweep
    const off = swingArm === 0 ? 1 : 0;
    const offSh = off === 0 ? shL : shR;
    ha[off] = [offSh[0] - cosA * 2.2, offSh[1] + 4 - sinA * 1.2];
  }
  // elbows from IK, bending away from the body
  const el: [XY, XY] = [
    ik2(shL, ha[0], UPPER_ARM, FORE_ARM, side ? (flip ? 1 : -1) : -1)[0],
    ik2(shR, ha[1], UPPER_ARM, FORE_ARM, side ? (flip ? 1 : -1) : 1)[0],
  ];
  ha = [ik2(shL, ha[0], UPPER_ARM, FORE_ARM, 1)[1], ik2(shR, ha[1], UPPER_ARM, FORE_ARM, 1)[1]];
  if (sickle) sickle.at = ha[swingArm];
  const key = [view, flip ? 1 : 0, yaw, s.lit ? 1 : 0, s.seated ? 1 : 0, s.attack, s.swingKind, q(hip[0]), q(hip[1]), q(chest[0]), q(chest[1]), q(head[0]), q(head[1]),
    ...ft.flat().map(q), ...kn.flat().map(q), ...el.flat().map(q), ...ha.flat().map(q),
    sickle ? Math.round(sickle.ang * 16) + ':' + sickle.len : '-', trail.length, s.wet ? 'w' : ''].join(',');
  return { view, flip, yaw, hip, chest, neck, head, sh: [shL, shR], el, ha, kn, ft, sickle, trail, swingArm, lit: s.lit, key, wet: !!s.wet } as any;
}

/* ---------------------------------------------------------------- paint --- */

export const INK = {
  // A redder coat separates the forager from yellow-green turf; dark hair
  // keeps the head readable without adding a halo or changing the mask.
  outline: P.o, skin: '#f0c394', tunic: '#ed884c', tunicDark: '#a34837', boot: P.b, hair: '#3d2c29',
  scarf: P.k, scarfDark: P.K, steel: P.m, steelDark: P.M, edge: P.w, eye: P.d, flame: P.y,
};

/* THE SHAPE THE LIGHT SEES IS THE SHAPE THAT WAS BUILT (LS-199).
 *
 * A body's relief came from `domeFromMask`: a distance field over the
 * SILHOUETTE, bulged into a dome. That is one dome for a whole figure, so an
 * arm crossing a chest was lit as part of one lump, a turtle's shell and its
 * legs shared a single curve, and every limb's roundness was inferred from an
 * outline rather than known. The primitives are solids -- a capsule is a
 * cylinder and a disc is an ellipsoid -- so each one can say which way its
 * surface points at every pixel it paints, and the painter now records that
 * as it goes. The light gets the real geometry, per limb, for nothing.
 *
 * `n` holds three floats per pixel and is written by whichever primitive owns
 * the pixel last, which is the one the eye sees. */
export type Grid = { w: number; h: number; px: (string | null)[]; n?: Float32Array };
export const grid = (w: number, h: number): Grid => ({ w, h, px: new Array(w * h).fill(null), n: new Float32Array(w * h * 3) });
export const put = (gr: Grid, x: number, y: number, c: string) => { if (x >= 0 && y >= 0 && x < gr.w && y < gr.h) gr.px[y * gr.w + x] = c; };
/** Paint a pixel and say which way its surface faces. */
const putN = (gr: Grid, x: number, y: number, c: string, nx: number, ny: number, nz: number) => {
  if (x < 0 || y < 0 || x >= gr.w || y >= gr.h) return;
  gr.px[y * gr.w + x] = c;
  if (!gr.n) return;
  const i = (y * gr.w + x) * 3;
  const l = Math.hypot(nx, ny, nz) || 1;
  gr.n[i] = nx / l; gr.n[i + 1] = ny / l; gr.n[i + 2] = nz / l;
};

/** A capsule from a to b, `w` wide, snapped: a pixel is in when its centre is
 *  within w/2 of the segment. */
export function cap(gr: Grid, a: XY, b: XY, w: number, c: string) {
  const r = w / 2;
  const x0 = Math.floor(Math.min(a[0], b[0]) - r), x1 = Math.ceil(Math.max(a[0], b[0]) + r);
  const y0 = Math.floor(Math.min(a[1], b[1]) - r), y1 = Math.ceil(Math.max(a[1], b[1]) + r);
  const dx = b[0] - a[0], dy = b[1] - a[1], L2 = dx * dx + dy * dy || 1;
  for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
    const px = x + 0.5, py = y + 0.5;
    const t = Math.max(0, Math.min(1, ((px - a[0]) * dx + (py - a[1]) * dy) / L2));
    const qx = a[0] + dx * t, qy = a[1] + dy * t;
    const off = Math.hypot(px - qx, py - qy);
    if (off > r) continue;
    // a capsule is a cylinder: across it the surface turns, along it it does
    // not, and what is left over faces the viewer
    const u = (px - qx) / r, v2 = (py - qy) / r;
    putN(gr, x, y, c, u, v2, Math.sqrt(Math.max(0.04, 1 - u * u - v2 * v2)));
  }
}
export function disc(gr: Grid, cx: number, cy: number, rx: number, ry: number, c: string) {
  for (let y = Math.floor(cy - ry); y <= Math.ceil(cy + ry); y++) for (let x = Math.floor(cx - rx); x <= Math.ceil(cx + rx); x++) {
    const u = (x + 0.5 - cx) / rx, v = (y + 0.5 - cy) / ry;
    if (u * u + v * v > 1) continue;
    // an ellipsoid: the normal is the point on the unit sphere it stands over
    putN(gr, x, y, c, u, v, Math.sqrt(Math.max(0.04, 1 - u * u - v * v)));
  }
}

/** Paint a pose into a grid of colour names. Back to front. */
/** WHAT IS WORN SHOWS (LS-239): the tunic takes the body piece's colour, the boots the treads'. */
const TUNIC: Record<string, [string, string]> = { hide: ['#7a4b2e', '#4a2c1a'], plate: ['#8c9097', '#4e535c'], mantle: ['#4f6b2f', '#2f4520'] };
export function paintPose(p: Pose): Grid {
  const gr = grid(RIG_W, RIG_H);
  const [bodyG, feetG] = (p.gear || '').split('+');
  const tint = TUNIC[bodyG || ''], tun = tint ? tint[0] : INK.tunic, tunD = tint ? tint[1] : INK.tunicDark, boot = feetG === 'treads' ? '#c9b07a' : INK.boot;
  const side = p.view === 'side';
  const fx = p.flip ? -1 : 1;              // which way the figure faces
  const farLeg = 1, nearLeg = 0;
  const farArm = side ? 1 : (p.view === 'back' ? -1 : -1);
  // far leg and far arm first, a shade darker
  cap(gr, p.hip, p.kn[farLeg], 2.2, tunD); cap(gr, p.kn[farLeg], p.ft[farLeg], 2, boot);
  if (side) { cap(gr, p.sh[0], p.el[0], 2, tunD); cap(gr, p.el[0], p.ha[0], 1.8, INK.skin); }
  // the trunk
  cap(gr, p.hip, p.chest, 4.6, tun);
  disc(gr, p.chest[0], p.chest[1], 2.6, 2.2, tun);
  // near leg
  cap(gr, p.hip, p.kn[nearLeg], 2.4, tun); cap(gr, p.kn[nearLeg], p.ft[nearLeg], 2, boot);
  if (!side) { cap(gr, p.hip, p.kn[1], 2.4, tun); cap(gr, p.kn[1], p.ft[1], 2, boot); }
  /* Clothing follows the posed trunk, including rolls and knockback. */
  const trunk = sub(p.chest, p.hip), trunkL = Math.hypot(...trunk) || 1;
  const across: XY = [-trunk[1] / trunkL, trunk[0] / trunkL];
  const waist = add(p.hip, mul(trunk, 0.22));
  cap(gr, add(waist, mul(across, -1.8)), add(waist, mul(across, 1.8)), 1, INK.boot);
  if (p.view === 'front') disc(gr, waist[0] + p.yaw * 0.5, waist[1], 0.7, 0.7, INK.steelDark);
  // the scarf, then the head
  if (p.view !== 'back') {
    const knot = add(p.neck, mul(sub(p.chest, p.neck), 0.7));
    cap(gr, [p.neck[0] - 1.5, p.neck[1]], knot, 1.6, INK.scarfDark);
    cap(gr, knot, [p.neck[0] + 1.5, p.neck[1]], 1.6, INK.scarf);
  }
  cap(gr, [p.neck[0] - 1.5, p.neck[1]], [p.neck[0] + 1.5, p.neck[1]], 2, INK.scarf);
  disc(gr, p.head[0], p.head[1], 3, 3, INK.skin);
  // hair: the crown, and the back of the head in side and back views
  disc(gr, p.head[0], p.head[1] - 1.3, 3, 1.8, INK.hair);
  if (p.view === 'back') disc(gr, p.head[0], p.head[1], 3, 3, INK.hair);
  // a turned head shows a cheek on the way it faces and hair on the way it came
  if (p.yaw) {
    if (p.view === 'back') disc(gr, p.head[0] + p.yaw * 1.5, p.head[1] + 0.3, 1.3, 2.1, INK.skin);
    else disc(gr, p.head[0] - p.yaw * 1.7, p.head[1] - 0.1, 1.4, 2.4, INK.hair);
  }
  if (side) disc(gr, p.head[0] - 1.6 * fx, p.head[1] - 0.2, 1.6, 2.6, INK.hair);
  // One connected crown highlight keeps the hair a mass at native scale.
  cap(gr, [p.head[0] - 1.4, p.head[1] - 2.1], [p.head[0] + 0.5, p.head[1] - 2.1], 1, '#79543b');
  // eyes
  if (p.view === 'front' && p.yaw) { put(gr, Math.round(p.head[0] + p.yaw * 1.8), Math.round(p.head[1] + 0.2), INK.eye); put(gr, Math.round(p.head[0] + p.yaw * 0.1), Math.round(p.head[1] + 0.2), INK.eye); }
  else if (p.view === 'front') { put(gr, Math.round(p.head[0] - 1.5), Math.round(p.head[1] + 0.2), INK.eye); put(gr, Math.round(p.head[0] + 0.5), Math.round(p.head[1] + 0.2), INK.eye); }
  if (side) put(gr, Math.round(p.head[0] + 1.2 * fx), Math.round(p.head[1] + 0.2), INK.eye);
  // the near arm (the right one in side view; both in front and back)
  const arms = side ? [1] : [0, 1];
  for (const i of arms) { cap(gr, p.sh[i], p.el[i], 2, tun); cap(gr, p.el[i], p.ha[i], 1.8, INK.skin); }
  // THE SMEAR (LS-207): the blade's path through the sweep, several
  // segments behind the blade, the farthest thinnest and darkest. Vijay: "we
  // need smears for all of the sword swipe animations". Drawn oldest first so
  // the bright, wide, newest segment lies on top.
  for (const tr of [...(p.trail || [])].reverse()) {
    const dx = Math.cos(tr.ang), dy = Math.sin(tr.ang);
    cap(gr, [tr.at[0] + dx * 3.5, tr.at[1] + dy * 3.5], [tr.at[0] + dx * tr.len, tr.at[1] + dy * tr.len], tr.w ?? 1.5, tr.dim ? INK.steelDark : INK.steel);
  }
  /* THE WEAPON IN HAND IS THE ONE WIELDED (LS-238; Vijay: 'visuals when equipping'). The painter drew a sickle whatever
   * the bag said. By kind now: a straight shaft and a pale leaf for the spear, a haft and a bitted head for the axe,
   * a haft and a flame for the torch, a closed hand for fists; and at rest each hangs where it would. */
  const kind = p.weapon || 'sickle';
  if (p.sickle) {
    const { at, ang, len } = p.sickle;
    const dx = Math.cos(ang), dy = Math.sin(ang), nx = -dy, ny = dx;
    if (kind === 'spear') {
      const L = len + 5;
      cap(gr, [at[0] - dx * 3, at[1] - dy * 3], [at[0] + dx * (L - 3), at[1] + dy * (L - 3)], 1.5, INK.boot);
      cap(gr, [at[0] + dx * (L - 3), at[1] + dy * (L - 3)], [at[0] + dx * L, at[1] + dy * L], 1.9, INK.steel);
      put(gr, Math.round(at[0] + dx * L), Math.round(at[1] + dy * L), INK.edge);
    } else if (kind === 'axe') {
      const L = len - 2;
      cap(gr, at, [at[0] + dx * L, at[1] + dy * L], 2.0, INK.boot);
      cap(gr, [at[0] + dx * (L - 2) + nx * 2.4, at[1] + dy * (L - 2) + ny * 2.4], [at[0] + dx * (L - 2) - nx * 1.2, at[1] + dy * (L - 2) - ny * 1.2], 3.0, INK.steel);
      cap(gr, [at[0] + dx * (L - 1) + nx * 2.8, at[1] + dy * (L - 1) + ny * 2.8], [at[0] + dx * (L - 1) - nx * 1.4, at[1] + dy * (L - 1) - ny * 1.4], 1.2, INK.edge);
    } else if (kind === 'torch') {
      const L = len - 3;
      cap(gr, at, [at[0] + dx * L, at[1] + dy * L], 2.0, INK.boot);
      disc(gr, at[0] + dx * (L + 1), at[1] + dy * (L + 1), 2.0, 2.4, INK.flame); put(gr, Math.round(at[0] + dx * (L + 1)), Math.round(at[1] + dy * (L + 1) - 1), INK.edge);
    } else if (kind === 'fist') {
      disc(gr, at[0] + dx * 1.5, at[1] + dy * 1.5, 1.7, 1.5, INK.skin);   // a closed hand, a little past the wrist
    } else {
      cap(gr, at, [at[0] + dx * 3, at[1] + dy * 3], 2.2, INK.boot);      // the haft
      let prev: XY = [at[0] + dx * 3, at[1] + dy * 3];
      for (let k = 1; k <= 5; k++) {
        const s = 3 + (len - 3) * (k / 5), bend = 0.26 * s * s / len;
        const pt: XY = [at[0] + dx * s + nx * bend, at[1] + dy * s + ny * bend];
        cap(gr, prev, pt, k === 5 ? 1.4 : 2.3, k === 5 ? INK.edge : INK.steel);
        prev = pt;
      }
    }
  } else if (kind === 'spear') {
    // slung across the back: a shaft from hip to shoulder, the leaf above it
    const hx = p.hip[0] + (side ? -2.5 * fx : 3), hy = p.hip[1] + 0.5;
    cap(gr, [hx, hy + 2], [hx + (side ? -fx : 1) * 1.5, hy - 8], 1.2, INK.boot); put(gr, Math.round(hx + (side ? -fx : 1) * 1.7), Math.round(hy - 9), INK.steel);
  } else if (kind === 'axe') {
    // at the hip: the head
    const hx = p.hip[0] + (side ? -2.5 * fx : 3), hy = p.hip[1] + 0.5;
    put(gr, Math.round(hx), Math.round(hy), INK.steel); put(gr, Math.round(hx), Math.round(hy) + 1, INK.steel); put(gr, Math.round(hx) + (side ? -fx : 1), Math.round(hy), INK.steelDark);
  } else if (kind === 'torch' || kind === 'fist') {
    // nothing hangs: a lit torch is in the off hand already, and hands are hands
  } else {
    // hanging at the hip: a hook of steel
    const hx = p.hip[0] + (side ? -2.5 * fx : 3), hy = p.hip[1] + 0.5;
    put(gr, Math.round(hx), Math.round(hy), INK.steelDark); put(gr, Math.round(hx), Math.round(hy) + 1, INK.steel);
  }
  // fire in the off hand
  if (p.lit) { const h = p.ha[side ? 1 : 0]; disc(gr, h[0], h[1] - 2, 1.6, 2, INK.flame); put(gr, Math.round(h[0]), Math.round(h[1] - 3.5), INK.edge); }
  outline(gr, INK.outline);
  return gr;
}

/** The outline pass: every empty cell touching a filled one takes `col`. */
export function outline(gr: Grid, col: string, opaqueOnly = false) {
  const out = gr.px.slice();
  /* `opaqueOnly`: a translucent cell -- a cast shadow on the ground -- is not a body and must not be drawn round, or
   * the shadow reads as a hole cut in the floor (2026-09-05, when the shadows were cast clear of their bodies). */
  const solid = (c: any) => !!c && (!opaqueOnly || String(c).length <= 7);
  for (let y = 0; y < gr.h; y++) for (let x = 0; x < gr.w; x++) {
    if (gr.px[y * gr.w + x]) continue;
    const n = (x > 0 && solid(gr.px[y * gr.w + x - 1])) || (x < gr.w - 1 && solid(gr.px[y * gr.w + x + 1]))
      || (y > 0 && solid(gr.px[(y - 1) * gr.w + x])) || (y < gr.h - 1 && solid(gr.px[(y + 1) * gr.w + x]));
    if (n) out[y * gr.w + x] = col;
  }
  gr.px = out;
}

/** Bake a painted grid to a canvas and a mask; `white` paints every cell paper (the hit flash). */
export function bakeGrid(gr: Grid, white = false) {
  const cell = atlasCell(gr.w, gr.h);   // a cell of a shared atlas, not a canvas of its own (LS-235)
  const c = cell.canvas, sx = cell.sx, sy = cell.sy;
  const g = cell.g;
  const mask = new Uint8Array(gr.w * gr.h);
  /* ONE putImageData, NOT A fillRect A PIXEL (Vijay's phone at q0): fourteen
   * animating bodies were two thousand fillRects a frame here. A hex colour
   * goes straight into the image's bytes; anything else falls back to the rect. */
  if (g) {
    const img = g.createImageData(gr.w, gr.h), d = img.data;
    const late: [number, number, string][] = [];
    for (let y = 0; y < gr.h; y++) for (let x = 0; x < gr.w; x++) {
      const i = y * gr.w + x, col = gr.px[i];
      if (!col) continue;
      mask[i] = 1;
      const hex = white ? '#fdf6e6' : col;
      if (hex.length === 7 || hex.length === 9) {
        const v = parseInt(hex.slice(1), 16), o = i * 4;
        if (hex.length === 7) { d[o] = v >> 16; d[o + 1] = (v >> 8) & 255; d[o + 2] = v & 255; d[o + 3] = 255; }
        else { d[o] = v >>> 24; d[o + 1] = (v >> 16) & 255; d[o + 2] = (v >> 8) & 255; d[o + 3] = v & 255; }
      } else late.push([x, y, hex]);
    }
    g.putImageData(img, sx, sy);
    for (const [x, y, hex] of late) { g.fillStyle = hex; g.fillRect(sx + x, sy + y, 1, 1); }
  }
  /* THE NORMALS COME OUT WITH THE PIXELS (LS-199). Each primitive said which
   * way its surface pointed as it painted (see `grid`), so the light can have
   * the real geometry -- per limb, per lobe -- instead of one dome inferred
   * from the whole silhouette. Packed the way `light.ts` packs every other
   * normal: xy into the low channels, z in blue. */
  let normals: Uint8ClampedArray | null = null;
  if (gr.n) {
    normals = new Uint8ClampedArray(gr.w * gr.h * 4);
    for (let i = 0; i < gr.w * gr.h; i++) {
      if (!mask[i]) continue;
      const o = i * 4, nx = gr.n[i * 3], ny = gr.n[i * 3 + 1], nz = gr.n[i * 3 + 2];
      normals[o] = Math.round((nx * 0.5 + 0.5) * 255);
      normals[o + 1] = Math.round((ny * 0.5 + 0.5) * 255);
      normals[o + 2] = Math.round(Math.max(0, nz) * 255);
      normals[o + 3] = 255;
    }
  }
  // a rig canvas carries its own outline and is authored at the size it is drawn
  (c as any).outlined = true; (c as any).rigged = true;
  return { canvas: c, sx, sy, mask, normals };
}

/* THE RIG LIGHTS ITSELF (LS-184). A body's relief used to come from the
 * frame sprite's mask, which the rig no longer draws, so the shape the light
 * saw and the shape on the glass disagreed. This is the dome of the rig's own
 * mask, cached by the pose key, for the relief pass to stamp. */
const normalCache = new Map<string, any>();
onAtlasReset(() => normalCache.clear());
export function maskNormals(key: string, mask: Uint8Array, w: number, h: number, real?: Uint8ClampedArray | null) {
  let c = normalCache.get(key);
  if (c !== undefined) return c;
  c = null;
  if (typeof document !== 'undefined') {
    // the geometry's own normals when the painter recorded them; the dome of
    // the silhouette only for art that was not built out of primitives
    const rgba = real || domeFromMask(mask, w, h);
    const cell = atlasCell(w, h);   // LS-235: a cell of a shared atlas
    const g = cell.g;
    if (g && g.createImageData) {
      const img = g.createImageData(w, h);
      img.data.set(rgba);
      g.putImageData(img, cell.sx, cell.sy);
      c = { canvas: cell.canvas, sx: cell.sx, sy: cell.sy, w, h };
    }
  }
  if (normalCache.size > 3000) normalCache.clear();   // cells of an atlas now, not canvases (LS-235)
  normalCache.set(key, c);
  return c;
}

/** `on: false` puts every sprite back (a debugging switch, see `game.rig`). */
export const rigFlags = { on: true };

/* ---------------------------------------------------------------- cache --- */

/* THE CELL IT IS PACKED INTO IS PART OF THE ANSWER. This said `{ canvas, mask }` while every entry built below
 * carries `sx`, `sy`, `w` and `h` as well -- the sprite lives in a shared atlas, so where it sits in that canvas
 * is not optional detail, it is how you draw it. Seventeen reads of `.sx`/`.sy` in `entities.ts` were errors
 * against a type that was simply wrong (2026-09-06). */
const cache = new Map<string, ReturnType<typeof bakeGrid> & { w: number; h: number }>();

/** The rendered pose as a canvas (and its mask), cached by pose key. */
onAtlasReset(() => cache.clear());
export function rigSprite(p: Pose) {
  let hit = cache.get(p.key);
  if (hit) return hit;
  // Keep the painter's per-limb normals with its colors, as creature rigs do.
  const gr = paintPose(p);
  if ((p as any).wet) for (let i = 0; i < gr.px.length; i++) if (gr.px[i]) gr.px[i] = wetTone(gr.px[i], 0.85);   // the wet state (2026-09-11)
  hit = { ...bakeGrid(gr), w: RIG_W, h: RIG_H };
  if (cache.size > 3000) cache.clear();   // the player's poses are many (a continuous stride phase); a small cap was churn
  cache.set(p.key, hit);
  return hit;
}

export function rigCacheSize() { return cache.size; }
